import React, { useEffect, useState } from "react";
import {
  Container,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { FieldArray, Formik, Form } from "formik";
import * as Yup from "yup";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import axios_api from "src/api/axios";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { CKRichTextEditor } from "src/components/ck-editor/ck-editor";
import { MultiSelectWithSearch } from "src/components/multi-select-with-search/multi-select-with-search";

const initialValues = {
  name: "",
  websource_group: "",
  show_in: "",
  order_by: "",
  is_active: false,
  title: "",
  description: "",
  seo_title: "",
  seo_description: "",
  og_title: "",
  og_description: "",
  twitter_title: "",
  twitter_description: "",
  full_description: "",
  script: "",
  conditions: [{ key_name: "", value: "", order_by: "" }],
};

const validationSchema = Yup.object({
  name: Yup.string().required("Required"),
  websource_group: Yup.string().required("Required"),
  show_in: Yup.string(),
  order_by: Yup.string().required("Required"),
  is_active: Yup.boolean(),
  title: Yup.string(),
  description: Yup.string(),
  seo_title: Yup.string(),
  seo_description: Yup.string(),
  og_title: Yup.string(),
  og_description: Yup.string(),
  twitter_title: Yup.string(),
  twitter_description: Yup.string(),
  full_description: Yup.string(),
  script: Yup.string(),
  conditions: Yup.array().of(
    Yup.object().shape({
      key_name: Yup.string().required("Required"),
      value: Yup.string().required("Required"),
      order_by: Yup.string().required("Required"),
    })
  ),
});

function CreateStaticUrls() {
  const [groupOptions, setGroupOptions] = useState([]);
  const [keyOptions, setKeyOptions] = useState([]);
  const [showInOptions, setShowInOptions] = useState([]);
  const [initialFormValues, setInitialFormValues] = useState(initialValues);
  const [loading, setLoading] = useState(true);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const params_ = useParams();
  const navigate = useNavigate();
  const instance_id = params_.id || null;
  const instance_operation = instance_id ? "update" : "create";

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [responseData] = await Promise.all([
          axios_api.get(
            "/dashboard/filters/listing/?action=web-source-url-group|web-source-condition|web-source-showin"
          )
        ]);
        setGroupOptions(responseData.data.records.web_source_url_group);
        setKeyOptions(responseData.data.records.web_source_condition);
        if (responseData?.data?.records?.web_source_showin) {
          const apiShowIn = responseData.data.records.web_source_showin;
          const normalized = apiShowIn.map((item) =>
            typeof item === "string"
              ? { id: item, name: item.charAt(0).toUpperCase() + item.slice(1) }
              : item
          );
          setShowInOptions(normalized);
        } else {
          // Fallback to static values if API key differs or missing
          setShowInOptions([
            { id: "home", name: "Home" },
            { id: "footer", name: "Footer" },
            { id: "contact", name: "Contact" },
          ]);
        }
      } catch (err) {
        console.error("Failed to load dropdown data");
      }
    };

    const fetchInstance = async () => {
      if (instance_operation === "update") {
        try {
          const res = await axios_api.get(
            `/web-source-url/manage/${instance_id}/details/`
          );
          const data = res.data.record;
          setInitialFormValues({
            name: data.name || "",
            websource_group: data.websource_group ? data.websource_group.map(g => g.id).join("***") : "",
            // API provides show_in as array of strings like ["home", "footer"]
            show_in: Array.isArray(data.show_in) ? data.show_in.join("***") : "",
            order_by: data.order_by || "",
            is_active: data.is_active,
            title: data.title || "",
            description: data.description || "",
            full_description: data.full_description || "",
            script: data.script || "",
            seo_title: data.seo_title || "",
            seo_description: data.seo_description || "",
            og_title: data.og_title || "",
            og_description: data.og_description || "",
            twitter_title: data.twitter_title || "",
            twitter_description: data.twitter_description || "",
            conditions:
              data.conditions?.length > 0
                ? data.conditions.map((c) => ({
                    key_name: c.key_name || "",
                    value: c.value || "",
                    order_by: c.order_by || "",
                  }))
                : [{ key_name: "", value: "", order_by: "" }],
          });
        } catch (err) {
          Swal.fire("Error", "Failed to fetch Static URL data.", "error");
        }
      }
    };

    Promise.all([fetchOptions(), fetchInstance()]).finally(() =>
      setLoading(false)
    );
  }, [instance_id, instance_operation]);

  const handleSubmit = async (values, actions) => {
    const payload = {
      name: values.name,
      websource_group: values.websource_group.split("***").filter(id => id),
      show_in: values.show_in ? values.show_in.split("***").filter(id => id) : [],
      order_by: values.order_by,
      is_active: values.is_active ? true : false,
      title: values.title,
      description: values.description,
      seo_title: values.seo_title,
      seo_description: values.seo_description,
      og_title: values.og_title,
      og_description: values.og_description,
      twitter_title: values.twitter_title,
      twitter_description: values.twitter_description,
      full_description: values.full_description,
      script: values.script,
      conditions: values.conditions.map((c) => ({
        key_name: c.key_name,
        value: c.value,
        order_by: c.order_by,
      })),
    };

    const url =
      instance_operation === "create"
        ? "/web-source-url/manage/"
        : `/web-source-url/manage/${instance_id}/`;

    try {
      const response = await axios_api.post(url, JSON.stringify(payload), {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.result === "success") {
        const confirmation = await Swal.fire({
          text: response.data.message || "Static URL saved successfully.",
          icon: "success",
          showConfirmButton: true,
        });

        if (confirmation.isConfirmed) {
          navigate("/static-urls/");
        } else if (instance_operation === "create") {
          actions.resetForm();
        }
      } else if (response.data.result === "failure") {
        const errors = response.data.errors || {};
        Object.keys(errors).forEach((key) => {
          actions.setFieldError(key, errors[key]);
        });
        Swal.fire(
          "Validation Error",
          "Please correct the highlighted fields.",
          "warning"
        );
      }
    } catch (err) {
      Swal.fire("Error", "Something went wrong. Please try again.", "error");
    } finally {
      actions.setSubmitting(false);
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Container>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={5}
      >
        <Typography variant="h4">
          {instance_operation === "create" ? "Create" : "Update"} Static URLs
        </Typography>
      </Stack>

      <Formik
        enableReinitialize
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          const { values, handleChange, touched, errors } = formik;
          return (
            <Form>
              <Stack spacing={2}>
                <TextField
                  label="Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />

                <MultiSelectWithSearch
                  name="websource_group"
                  formik={formik}
                  options={groupOptions}
                  withSubmit={false}
                  allRequired={true}
                  required={true}
                  setIsAllSelected={setIsAllSelected}
                />

                <MultiSelectWithSearch
                  name="show_in"
                  label="Show In"
                  formik={formik}
                  options={showInOptions}
                  withSubmit={false}
                  allRequired={true}
                  required={false}
                  disabled={!values.websource_group}
                  setIsAllSelected={setIsAllSelected}
                />

                <TextField
                  label="Order By"
                  name="order_by"
                  value={values.order_by}
                  onChange={handleChange}
                  error={touched.order_by && Boolean(errors.order_by)}
                  helperText={touched.order_by && errors.order_by}
                />

                <TextField
                  label="Title (H1 Tag)"
                  name="title"
                  value={values.title}
                  onChange={handleChange}
                  error={touched.title && Boolean(errors.title)}
                  helperText={touched.title && errors.title}
                />

                <Typography>Description (Featured Text)</Typography>
                <CKRichTextEditor
                  formik={formik}
                  name="description"
                  required={true}
                  showName={false}
                />

                <TextField
                  label="SEO Title"
                  name="seo_title"
                  value={values.seo_title}
                  onChange={handleChange}
                  error={touched.seo_title && Boolean(errors.seo_title)}
                  helperText={touched.seo_title && errors.seo_title}
                />
                <TextField
                  label="SEO Description"
                  name="seo_description"
                  value={values.seo_description}
                  onChange={handleChange}
                  error={
                    touched.seo_description && Boolean(errors.seo_description)
                  }
                  helperText={touched.seo_description && errors.seo_description}
                />
                <TextField
                  label="OG Title"
                  name="og_title"
                  value={values.og_title}
                  onChange={handleChange}
                  error={touched.og_title && Boolean(errors.og_title)}
                  helperText={touched.og_title && errors.og_title}
                />
                <TextField
                  label="OG Description"
                  name="og_description"
                  value={values.og_description}
                  onChange={handleChange}
                  error={
                    touched.og_description && Boolean(errors.og_description)
                  }
                  helperText={touched.og_description && errors.og_description}
                />
                <TextField
                  label="Twitter Title"
                  name="twitter_title"
                  value={values.twitter_title}
                  onChange={handleChange}
                  error={touched.twitter_title && Boolean(errors.twitter_title)}
                  helperText={touched.twitter_title && errors.twitter_title}
                />
                <TextField
                  label="Twitter Description"
                  name="twitter_description"
                  value={values.twitter_description}
                  onChange={handleChange}
                  error={
                    touched.twitter_description &&
                    Boolean(errors.twitter_description)
                  }
                  helperText={
                    touched.twitter_description && errors.twitter_description
                  }
                />

                <Typography>Full Description</Typography>
                <CKRichTextEditor
                  formik={formik}
                  name="full_description"
                  required={false}
                  showName={false}
                />

                <Typography>Script</Typography>
                <TextField
                  label="Schema (JSON-LD)"
                  name="script"
                  value={values.script}
                  onChange={handleChange}
                  multiline
                  minRows={10}
                  fullWidth
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      name="is_active"
                      checked={values.is_active}
                      onChange={handleChange}
                    />
                  }
                  label="Is Active"
                />

                <Typography variant="h6" mt={2}>
                  Conditions
                </Typography>
                <FieldArray name="conditions">
                  {({ push, remove }) => (
                    <>
                      {values.conditions.map((_, index) => (
                        <Stack
                          key={index}
                          direction="row"
                          spacing={2}
                          alignItems="center"
                        >
                          <FormControl
                            sx={{ minWidth: 150 }}
                            error={
                              touched.conditions?.[index]?.key_name &&
                              Boolean(errors.conditions?.[index]?.key_name)
                            }
                          >
                            <InputLabel>Key</InputLabel>
                            <Select
                              name={`conditions[${index}].key_name`}
                              value={values.conditions[index].key_name}
                              onChange={handleChange}
                              label="Key"
                            >
                              {keyOptions.map((opt) => (
                                <MenuItem key={opt.id} value={opt.id}>
                                  {opt.name}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              {touched.conditions?.[index]?.key_name &&
                                errors.conditions?.[index]?.key_name}
                            </FormHelperText>
                          </FormControl>

                          <TextField
                            label="Value"
                            name={`conditions[${index}].value`}
                            value={values.conditions[index].value}
                            onChange={handleChange}
                            error={
                              touched.conditions?.[index]?.value &&
                              Boolean(errors.conditions?.[index]?.value)
                            }
                            helperText={
                              touched.conditions?.[index]?.value &&
                              errors.conditions?.[index]?.value
                            }
                          />

                          <TextField
                            label="Order By"
                            name={`conditions[${index}].order_by`}
                            value={values.conditions[index].order_by}
                            onChange={handleChange}
                            error={
                              touched.conditions?.[index]?.order_by &&
                              Boolean(errors.conditions?.[index]?.order_by)
                            }
                            helperText={
                              touched.conditions?.[index]?.order_by &&
                              errors.conditions?.[index]?.order_by
                            }
                          />

                          <IconButton
                            color="error"
                            onClick={() => remove(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      ))}

                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          push({ key_name: "", value: "", order_by: "" })
                        }
                      >
                        Add Condition
                      </Button>
                    </>
                  )}
                </FieldArray>

                <Button variant="contained" type="submit">
                  {instance_operation === "create" ? "Create" : "Update"}
                </Button>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
}

export default CreateStaticUrls;
